import { BreakpointObserver } from "@angular/cdk/layout";
import { CommonModule, DatePipe, DOCUMENT } from "@angular/common";
import {
    Component,
    Inject,
    OnInit,
    PLATFORM_ID
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { CloudinaryModule, placeholder } from "@cloudinary/ng";
import { Cloudinary, CloudinaryImage, Transformation } from "@cloudinary/url-gen";
import { opacity } from "@cloudinary/url-gen/actions/adjust";
import { format, quality } from "@cloudinary/url-gen/actions/delivery";
import { colorize } from "@cloudinary/url-gen/actions/effect";
import { source } from "@cloudinary/url-gen/actions/overlay";
import { crop, fill, minimumFit, scale } from "@cloudinary/url-gen/actions/resize";
import { Position } from "@cloudinary/url-gen/qualifiers";
import { auto } from "@cloudinary/url-gen/qualifiers/quality";
import { image } from "@cloudinary/url-gen/qualifiers/source";
import { Client, HeaderPageComponent } from "@interfaces";
import { GtagService } from "src/app/containers/gtag/gtag.service";
import { TypefaceLoaderComponent } from "src/app/containers/typefaces/typeface-loader/typeface-loader.component";
import { calculateButtonClasses, calculateButtonStyle } from "src/app/helpers/buttons";
import { parseColour } from "src/app/helpers/colour";
import { SubdomainService } from "src/app/rest/subdomain.service";
import { VideosService } from "src/app/rest/videos.service";
import { ComponentActionsService } from "../../services/component-actions.service";
import { AbstractVideoComponent } from "../abstract-video-component";
import { ApplyButtonComponent } from "../apply-button/apply-button.component";
import { CtaComponent } from "../cta/cta.component";
import { JobDetailsButtonComponent } from "../job-details-button/job-details-button.component";
import { HeaderVideoComponent } from "./header-video/header-video.component";
@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    standalone: true,
    imports: [
        CommonModule,
        CloudinaryModule,
        ApplyButtonComponent,
        JobDetailsButtonComponent,
        CtaComponent,
        TypefaceLoaderComponent,
        HeaderVideoComponent,
    ],
    providers: [DatePipe],
})
export class HeaderComponent extends AbstractVideoComponent implements OnInit {

    public component: HeaderPageComponent;
    public backgroundStyle = {};
    public buttonStyle = {};
    public buttonClasses = [];
    public textStyle = {};
    public textSize = "regular";
    public typeface = "typeface-regular";
    private client: Client;
    public img: CloudinaryImage;
    public imageWidth = 1536;
    public imageHeight = 512;
    public plugins = [placeholder({ mode: "predominant-color" })];
    public sanitizedContent: SafeHtml;

    constructor(
        private subdomain: SubdomainService,
        protected gtag: GtagService,
        protected breakpointObserver: BreakpointObserver,
        protected actions: ComponentActionsService,
        protected videosService: VideosService,
        private sanitizer: DomSanitizer,
        private datePipe: DatePipe,
        @Inject(PLATFORM_ID) protected platform,
        @Inject(DOCUMENT) protected document
    ) {
        super(videosService, actions, platform, document, breakpointObserver, gtag);
    }

    ngOnInit() {
        this.subdomain.getClient().subscribe((client: Client) => {
            this.client = client;
            this.setupAppearance();
            this.parseContent();
        });
    }

    public parseContent() {
        let htmlString = "";

        this.component.jobTextOverride = this.component.jobTextOverride?.replace(/{{jobTitle}}/g, this.job?.title);
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(
            /{{jobLocation}}/g,
            this.job?.locationLabel || this.job?.location
        );
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(/{{jobDescription}}/g, this.job?.content?.en);
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(/{{jobSummary}}/g, this.job?.summary?.en);
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(
            /{{jobCreatedAt}}/g,
            this.datePipe.transform(this.job?.createdAt)
        );
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(
            /{{jobUpdatedAt}}/g,
            this.datePipe.transform(this.job?.updatedAt)
        );
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(/{{jobCategory}}/g, this.job?.category);
        this.component.jobTextOverride = this.component.jobTextOverride?.replace(/{{pageTitle}}/g, this.page?.title);

        htmlString += `${this.component.jobTextOverride}</div>`;

        this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(htmlString);
    }

    private setupAppearance() {
        this.typeface = this.component.typeface || "typeface-regular";
        this.textSize = this.component.textSize || "regular";
        this.backgroundStyle = this.calculateBackgroundStyle();
        this.buttonStyle = calculateButtonStyle(this.component.buttonConfig, this.client);
        this.buttonClasses = calculateButtonClasses(this.component.buttonConfig);
        if (this.component.image) {
            this.calculateImage();
        }
    }

    private calculateBackgroundStyle() {
        let parsedColor,
            background,
            color = "white";

        parsedColor = parseColour("black");
        let [r, g, b] = parsedColor.values;
        let { overlayOpacity = 0.4, typeface } = this.component;

        if (this.component.textColour) {
            color = this.component.textColour;
        }

        if (!typeface) {
            typeface = this.job?.headingFont || this.page?.headingFont;
        }

        background = `rgba(${r},${g},${b},${overlayOpacity})`;

        return { background, color, "fontFamily": typeface ? typeface : "Inter, sans-serif" };
    }

    private calculateImage() {
        if (!this.component.image?.public_id && !this.component.mobileImage?.public_id) return;
        const cld = new Cloudinary({ cloud: { cloudName: "minirobot" } });
        let scaleFn: any = scale;
        this.imageHeight = 512;
        this.imageWidth = 1536;

        if (this.mobileMode) {
            this.imageWidth = 512;
        } else if(!this.row?.fullWidth) {
            this.imageWidth = 1536;
        } else {
            this.imageWidth = this.document?.documentElement?.clientWidth;
        }

        switch (this.component.minHeight) {
            case "small":
                this.imageHeight = 512;
                break;
            case "medium":
                this.imageHeight = Math.round(this.document?.documentElement?.clientHeight * 0.5);
                if(!this.imageHeight || isNaN(this.imageHeight)) {
                    this.imageHeight = 700;
                }
                break;
            case "large":
                this.imageHeight = this.document?.documentElement?.clientHeight;
                if(!this.imageHeight || isNaN(this.imageHeight)) {
                    this.imageHeight = 700;
                }
                break;
        }

        if (this.mobileMode) {
            this.imageHeight = 512;
        }

        switch (this.component.scaling) {
            case "fill":
                scaleFn = fill;
                break;
            case "crop":
                scaleFn = crop;
                break;
            case "fit":
                scaleFn = minimumFit;
                break;
            default:
                scaleFn = scale;
        }

        this.img = cld
            .image(
                this.mobileMode
                    ? this.component.mobileImage?.public_id ?? this.component.image?.public_id
                    : this.component.image?.public_id
            )
            .delivery(quality(auto()))
            .delivery(format(auto()))
            .resize(scaleFn().width(this.imageWidth).height(this.imageHeight));

        if (!this.published) {
            this.img.overlay(
                source(
                    image("voyse:assets:draft").transformation(
                        new Transformation().effect(colorize(20).color("white")).adjust(opacity(20))
                    )
                ).position(new Position().allowOverflow(false))
            );
        }
    }

    public scrollToRow() {
        this.document.getElementById(`template-row-${this.component.target}`).scrollIntoView({ behavior: "smooth" });
    }
}
